import {BaseModel} from '../base/BaseModel';

/**
 * @typedef Recipe
 *
 * @property {String<UUID>} productUuid
 * @property {String<UUID>} averageApplicationRate
 * @property {String<UUID>} totalApplication
 */

/**
 * @typedef ApplicationZone
 *
 * @property {String} category
 * @property {Number} area
 */

/**
 * @typedef Rate
 *
 * @property {String<UUID>} productUuid
 * @property {String<UUID>} isMaster
 * @property {String<UUID>} rates
 */

/**
 * @class DosedMapModel
 * @extends BaseModel
 *
 * @property {String<UUID>} attributes.uuid
 * @property {String<UUID>} attributes.cropSeasonUuid - crop season
 * @property {moment} attributes.recommendationDate - recommendation date
 * @property {String<URL>} attributes.imageUrl - created image
 * @property {String<URL>} attributes.geoserverUrl - url for geoserver
 * @property {moment} attributes.dateCreated - creation date
 * @property {moment} attributes.sourceDate - source map date
 * @property {Number} attributes.waterAverageApplicationRate - calculated average water rate
 * @property {Number} attributes.waterTotalApplication - calculated total water application
 * @property {Array<Recipe>} attributes.recipe - recipe for products
 * @property {ApplicationZone} attributes.applicationZones - application zones
 * @property {boolean} attributes.isHighFlow - set by the user, based on nozzles
 * @property {String} attributes.applicationType - set by the user, based on select
 * @property {Array<Number>} attributes.waterRates - set by the user, based on water input field
 * @property {Array<Rate>} attributes.rates - set by the user, base on product input fields
 * @property {GeoJSON<FeatureCollection>} attributes.geometry - created by the service, supported in $extends
 * @property {String<UUID>} attributes.sourceMapUuid - can be set by the user, if null, service uses latest map
 * @property {String} attributes.checksum - calculated by the server
 * @property {String} attributes.doseKeyOverride  - (optional) Overrides automatic setting of doseKey.
 *                                                  If undefined, service will set doseKey automatically
 *                                                  See DOSE_KEYS.
 * @property {String} attributes.doseKey  - dose key info, can be overriden by doseKeyOverride. See DOSE_KEYS.
 * @property {String} attributes.limitedVra  - limited VRA info from timing.
 * @property {String} attributes.specificLimitedVra  - timing info for specific limited VRA.
 * @property {String} attributes.overrideSpecificLimitedVra  - timing info for specific limited VRA to override.
 * @property {Array<Number>} attributes.masterVraRates  - master vra rates info from timing
 * @property {Array<Number>} attributes.subscriptionStatus  - subscription info. See SUBSCRIPTION_STATUS.
 * @property {Object} attributes.bufferZones                    - BZ info
 * @property {Moment} attributes.bufferZones.validFrom          - From when BZ is valid
 * @property {Moment} attributes.bufferZones.validTill          - Until when BZ is valid
 * @property {Moment} attributes.bufferZones.applicationDate    - Date for which BZ was calculated to be applied
 * @property {String} attributes.bufferZones.driftReductionCode - Drift reduction code. See DRIFT_RATES
 *
 */
export class DosedMapModel extends BaseModel {

    initialize () {

        this.microservice = 'mapping';
        this.resource = 'dosed-maps';
    }


    get defaults () {

        return {
            uuid: undefined,
            cropSeasonUuid: undefined,
            recommendationDate: undefined,
            imageUrl: undefined,
            geoserverUrl: undefined,
            dateCreated: undefined,
            sourceDate: undefined,
            waterAverageApplicationRate: undefined,
            waterTotalApplication: undefined,
            recipe: undefined,
            applicationZones: undefined,
            isHighFlow: undefined,
            applicationType: undefined,
            waterRates: undefined,
            rates: undefined,
            geometry: undefined,
            sourceMapUuid: undefined,
            checksum: undefined,
            doseKeyOverride: undefined,
            doseKey: undefined,
            limitedVra: undefined,
            specificLimitedVra: undefined,
            overrideSpecificLimitedVra: undefined,
            masterVraRates: undefined,
            subscriptionStatus: undefined,
            bufferZones: undefined,
            applicationSpotUuid: undefined
        };
    }

    parse (data) {

        if (_.isNil(data.recipeV2) || !_.isArray(data.recipeV2)) {

            return data;
        }

        if (data.bufferZones) {

            data.bufferZones = this.parseBufferZones(data.bufferZones);
        }

        data.recipeV2 = _.orderBy(data.recipeV2, ['type'], ['desc']);

        return data;
    }

    isFlat () {

        return (this.get('applicationType') === DosedMapModel.APPLICATION_TYPE.FLAT);
    }

    isNonFlat () {

        return (this.get('applicationType') !== DosedMapModel.APPLICATION_TYPE.FLAT);
    }

    parseBufferZones (bz) {

        if (!bz) {

            return null;
        }

        let flatCodes = _.values(DosedMapModel.DRIFT_RATES);

        return {
            driftReductionCode: (flatCodes.indexOf(bz.driftReductionCode) !== -1 ? bz.driftReductionCode : DosedMapModel.DRIFT_RATES.NONE),
            applicationDate: BaseModel.parseDate(bz.applicationDate),
            validFrom: BaseModel.parseDate(bz.validFrom),
            validTill: BaseModel.parseDate(bz.validTill),
            reductionFlag: bz.reductionFlag
        };
    }

    toJSON () {

        let attributes = _.assign({}, this.attributes);

        if (attributes.bufferZones) {

            attributes.bufferZones = {
                driftReductionCode: attributes.bufferZones.driftReductionCode,
                reductionFlag: attributes.bufferZones.reductionFlag,
                applicationDate: attributes.bufferZones.applicationDate.toISOString()
            };
        }

        delete attributes.creatorId;
        delete attributes.lastModifierId;

        return attributes;
    }

    getApplicationType() {

        let applicationType = DosedMapModel.APPLICATION_TYPE_NAMES.FUNGICIDE;

        if (this.get('applicationType') === DosedMapModel.APPLICATION_TYPE.VRA_HERB) {
            applicationType = DosedMapModel.APPLICATION_TYPE_NAMES.HERBICIDE;
        }

        return applicationType;
    }

    /**
     * @returns {string}
     */
    getActionType () {
        return DosedMapModel.ACTION_TYPES[_.head(this.get('actionTypes'))] ||
               DosedMapModel.ACTION_TYPES.FUNGICIDE_APPLICATION;
    }

    isOnOff () {
        return (this.get('applicationType') === DosedMapModel.APPLICATION_TYPE.ON_OFF);
    }

    static getApplicationTypeString (applicationType) {

        switch (applicationType) {

            case DosedMapModel.APPLICATION_TYPE.ON_OFF: return __('APPLICATION_TYPE.ON_OFF');
            case DosedMapModel.APPLICATION_TYPE.FLAT: return __('APPLICATION_TYPE.FLAT');
            case DosedMapModel.APPLICATION_TYPE.VRA: return __('APPLICATION_TYPE.VRA');
            case DosedMapModel.APPLICATION_TYPE.CUSTOM_VRA: return __('APPLICATION_TYPE.VRA');
            case DosedMapModel.APPLICATION_TYPE.VRA_HERB: return __('APPLICATION_TYPE.VRA_HERB');
        }
    }

    static get ZONES () {

        return {
            FLAT: 'FLAT',

            // Europe
            HIGH: 'HIGH',
            ABOVE_AVERAGE: 'ABOVE_AVERAGE',
            AVERAGE: 'AVERAGE',
            BELOW_AVERAGE: 'BELOW_AVERAGE',
            LOW: 'LOW',
            BUFFER_ZONE_TOTAL: 'BUFFER_ZONE_TOTAL',

            // Brazil/Argentina
            WEEDS: 'WEEDS',
            NO_WEEDS: 'NO_WEEDS',

            // Canada/US
            ON_OFF_1: 'ON_OFF_1',
            ON_OFF_2: 'ON_OFF_2',
            ON_OFF_3: 'ON_OFF_3',
            ON_OFF_4: 'ON_OFF_4',
            ON_OFF_5: 'ON_OFF_5',
            ON_OFF_6: 'ON_OFF_6',
            ON_OFF_7: 'ON_OFF_7',

            // Management zones.
            TRIAL_PLOT: 'TRIAL_PLOT',
            OBSTACLE: 'OBSTACLE',
            BIODIVERSITY_WINDOW: 'BIODIVERSITY_WINDOW',

            // Seeding zones
            // TODO verify all zones
            SEEDING_1: 'SEEDING_1',
            SEEDING_2: 'SEEDING_2',
            SEEDING_3: 'SEEDING_3',
            SEEDING_4: 'SEEDING_4',
            SEEDING_5: 'SEEDING_5',

            // CUSTOM_VRA USA and Canada
            VERY_HIGH: 'VERY_HIGH',
            VERY_LOW: 'VERY_LOW'
        };
    }

    /**
     * @static
     * Enumeration for doseKeyOverride property.
     *
     * Dose keys that can be used to override dose key.
     * Please note that AUTOMATIC is achived through setting
     * doseMapOverride to 'undefined'.
     */
    static get DOSE_KEYS () {

        return {
            AUTOMATIC: 'AUTOMATIC',
            HIGH: 'HIGH',
            MEDIUM: 'MEDIUM',
            LOW: 'LOW'
        };
    }

    /**
     * @static
     * Enumeration for subscriptionStatus property.
     */
    static get SUBSCRIPTION_STATUS () {

        return {
            SUBSCRIBED: 'SUBSCRIBED',
            NOT_SUBSCRIBED: 'NOT_SUBSCRIBED',
            TOO_SMALL: 'TOO_SMALL',
            OUT_OF_SEASON: 'OUT_OF_SEASON'
        };
    }

    /**
     * @static
     * Enumeration for applicaitonType property.
     */
    static get APPLICATION_TYPE () {

        return {
            ON_OFF: 'ON_OFF',
            FLAT: 'FLAT',
            VRA: 'VRA',
            VRA_HERB: 'VRA_HERB',
            CUSTOM_VRA: 'CUSTOM_VRA',

            // Seeding
            VARIABLE_RATE_SEEDING: 'VARIABLE_RATE_SEEDING',
            FLAT_SEEDING: 'FLAT_SEEDING',

            // UI Types
            VRA_HERB_ON_OFF: 'VRA_HERB_ON_OFF',
            VRA_HERB_2_RATE: 'VRA_HERB_2_RATE'
        };
    }

    static get TYPES () {

        return {
            PRODUCT: 'PRODUCT',
            ADDITIVE: 'ADDITIVE',
            WATER: 'WATER',
            VARIETY: 'VARIETY'
        };
    }

    static get DRIFT_RATES () {

        return {
            PERCENT_50: '50_PERCENT',
            PERCENT_75: '75_PERCENT',
            PERCENT_90: '90_PERCENT',

            '1STAR': '1STAR',
            '2STAR': '2STAR',
            '3STAR': '3STAR',

            NONE: 'NONE'
        };
    }

    static get APPLICATION_TYPE_NAMES () {

        return {
            FUNGICIDE: 'FUNGICIDE',
            HERBICIDE: 'HERBICIDE'
        };
    }

    static get ACTION_TYPES () {

        return {
            PLANT_GROWTH_REGULATOR_APPLICATION: 'PLANT_GROWTH_REGULATOR_APPLICATION',
            FUNGICIDE_APPLICATION: 'FUNGICIDE_APPLICATION',
            INSECTICIDE_APPLICATION: 'INSECTICIDE_APPLICATION',
            HERBICIDE_APPLICATION: 'HERBICIDE_APPLICATION',
            FERTILIZER_APPLICATION: 'FERTILIZER_APPLICATION',
            SEEDING: 'SEEDING',
            HARVESTING: 'HARVESTING'
        };
    }

    static get PRODUCT_CATEGORIES () {

        return {
            PLANT_GROWTH_REGULATOR_APPLICATION: 'PGR',
            FUNGICIDE_APPLICATION: 'F',
            INSECTICIDE_APPLICATION: 'I',
            HERBICIDE_APPLICATION: 'H',
            FERTILIZER_APPLICATION: 'FERTI',
        };
    }

    static get APPLICATION_MODES () {

        return {
            SPRAYING: 'SPRAYING',
            SPREADING: 'SPREADING'
        };
    }

    static serializeTaskName (applicationMode) {

        switch (applicationMode) {
            case DosedMapModel.APPLICATION_MODES.SPRAYING:
                return __('TASK_LIST_ITEM.SPRAY');
            case DosedMapModel.APPLICATION_MODES.SPREADING:
                return __('TASK_LIST_ITEM.SPREAD');
            default:
                return __('TASK_LIST_ITEM.SPRAY');
        }
    }

    static serializeTaskActionTypes (actionTypes) {

        return actionTypes.map((type) => {

            switch (type) {
                case DosedMapModel.ACTION_TYPES.PLANT_GROWTH_REGULATOR_APPLICATION:
                    return __('PLANT_GROWTH_REGULATOR');
                case DosedMapModel.ACTION_TYPES.FUNGICIDE_APPLICATION:
                    return __('FUNGICIDE');
                case DosedMapModel.ACTION_TYPES.INSECTICIDE_APPLICATION:
                    return __('INSECTICIDE');
                case DosedMapModel.ACTION_TYPES.HERBICIDE_APPLICATION:
                    return __('HERBICIDE');
                case DosedMapModel.ACTION_TYPES.FERTILIZER_APPLICATION:
                    return __('NUTRITION');
                case DosedMapModel.ACTION_TYPES.SEEDING:
                    return __('SEEDING');
                case DosedMapModel.ACTION_TYPES.HARVESTING:
                    return __('HARVESTING');
                default:
                    return '';
            }
        });
    }

    static serializeIcon (applicationMode) {

        switch (applicationMode) {
            case DosedMapModel.APPLICATION_MODES.SPRAYING:
                return 'df-icon-spray';
            case DosedMapModel.APPLICATION_MODES.SPREADING:
                return 'df-icon-spread';
            default:
                return 'df-icon-spray';
        }
    }
}


// WEBPACK FOOTER //
// ./shared/model/mapping/DosedMapModel.js